import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, TrendingDown, RefreshCw, Globe, MapPin } from "lucide-react";
import { getPortfolioMovers } from "../lib/api";

/**
 * Top Movers — horizontal bar chart of biggest daily % moves across all
 * holdings (combined India + US). Toggleable Gainers/Losers + Country.
 */
export default function TopMoversCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("gainers"); // gainers | losers
  const [country, setCountry] = useState("all"); // all | India | US
  const [range, setRange] = useState("1d"); // 1d | 7d | 30d | 6m

  // Skip stacking auto-refreshes on slow networks
  const isLoadingRef = React.useRef(false);

  const load = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      let res = await getPortfolioMovers(10, country, range);
      // Cold-start gracefully: ONE retry after 8s if backend is still warming.
      // (Earlier we did up to 3 retries with 30s of total waiting → caused the
      // dashboard to reflow when this card finally rendered. One retry is enough.)
      if ((res.data?.total_universe || 0) === 0) {
        await new Promise(r => setTimeout(r, 8000));
        res = await getPortfolioMovers(10, country, range);
      }
      setData(res.data);
    } catch (e) {
      console.error("movers load failed", e);
    }
    setLoading(false);
    isLoadingRef.current = false;
  }, [country, range]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s during market hours (or 5min outside).
  // Skip polls when tab is hidden — saves backend load + avoids page reflow on tab return.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      if (isLoadingRef.current) return;
      load();
    }, (data?.market_open ? 60 : 300) * 1000);
    return () => clearInterval(interval);
  }, [load, data?.market_open]);

  const rows = (tab === "gainers" ? data?.gainers : data?.losers) || [];
  const maxAbs = Math.max(1, ...rows.map(r => Math.abs(r.day_change_pct || 0)));

  const fmtVal = (v, c) => {
    if (v === null || v === undefined) return "—";
    const sign = v >= 0 ? "+" : "";
    if (c === "US") return `${sign}$${Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    return `${sign}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="top-movers-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {tab === "gainers"
            ? <TrendingUp size={16} className="text-emerald-400" />
            : <TrendingDown size={16} className="text-red-400" />
          }
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>
            Top Movers
          </h3>
          <span className="text-[10px] text-[var(--text-muted)]">
            · {range === "1d" ? "today" : range.toUpperCase()} · {data?.total_universe || 0} stocks
            {range === "1d" && data?.market_open === false && " · market closed"}
          </span>
        </div>
        <div className="flex gap-1.5 items-center flex-wrap">
          {/* Range toggle */}
          <div className="flex gap-0 bg-[var(--bg-base)] rounded p-0.5" data-testid="movers-range-group">
            {[
              { key: "1d", label: "1D" },
              { key: "7d", label: "7D" },
              { key: "30d", label: "30D" },
              { key: "6m", label: "6M" },
              { key: "52w", label: "52W" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setRange(key); setLoading(true); }}
                className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                  range === key
                    ? "bg-[var(--brand)]/15 text-[var(--brand)]"
                    : "text-[var(--text-muted)] hover:text-white"
                }`}
                data-testid={`movers-range-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Country filter */}
          <div className="flex gap-0 bg-[var(--bg-base)] rounded p-0.5">
            {[
              { key: "all", icon: Globe, label: "All" },
              { key: "India", icon: MapPin, label: "IN" },
              { key: "US", icon: MapPin, label: "US" },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setCountry(key)}
                className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-colors ${
                  country === key
                    ? "bg-[var(--brand)]/15 text-[var(--brand)]"
                    : "text-[var(--text-muted)] hover:text-white"
                }`}
                data-testid={`movers-country-${key}`}
              >
                <Icon size={10} /> {label}
              </button>
            ))}
          </div>
          {/* Gainers/Losers toggle */}
          <div className="flex gap-0 bg-[var(--bg-base)] rounded p-0.5">
            <button
              onClick={() => setTab("gainers")}
              className={`px-2.5 py-1 text-[11px] rounded transition-colors ${
                tab === "gainers"
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="movers-tab-gainers"
            >
              Gainers
            </button>
            <button
              onClick={() => setTab("losers")}
              className={`px-2.5 py-1 text-[11px] rounded transition-colors ${
                tab === "losers"
                  ? "bg-red-500/15 text-red-400"
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="movers-tab-losers"
            >
              Losers
            </button>
          </div>
          <button
            onClick={load}
            className="p-1.5 text-[var(--text-muted)] hover:text-white rounded hover:bg-white/5"
            title="Refresh"
            data-testid="movers-refresh"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="min-h-[290px]" data-testid="movers-body">
      {loading && rows.length === 0 ? (
        <div className="space-y-1.5">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => <div key={i} className="h-7 bg-white/5 animate-pulse rounded" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center text-[12px] text-[var(--text-muted)] py-24">
          {(data?.total_universe || 0) === 0
            ? "Computing today's moves… give it 30s and refresh."
            : `No ${tab} found${country !== "all" ? ` for ${country}` : ""} today.`}
        </div>
      ) : (
        <div className="space-y-1.5" data-testid={`movers-list-${tab}`}>
          {rows.map(r => {
            const pct = r.day_change_pct || 0;
            const widthPct = Math.min(100, (Math.abs(pct) / maxAbs) * 100);
            const up = pct >= 0;
            return (
              <div key={`${r.symbol}-${r.country}`} className="relative group" data-testid={`mover-${r.symbol}`}>
                {/* Bar background */}
                <div className="relative h-7 bg-[var(--bg-base)] rounded overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full transition-all duration-500 ease-out ${
                      up ? "bg-emerald-500/15" : "bg-red-500/15"
                    }`}
                    style={{ width: `${widthPct}%` }}
                  />
                  <div className={`absolute top-0 left-0 h-full ${up ? "border-r border-emerald-500/40" : "border-r border-red-500/40"}`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
                {/* Foreground content */}
                <div className="absolute inset-0 flex items-center justify-between px-2.5 text-[12px]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-mono font-semibold text-white truncate">{r.symbol}</span>
                    <span className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider">{r.country}</span>
                    <span className="text-[10px] text-[var(--text-muted)] truncate">{r.sector}</span>
                  </div>
                  <div className="flex items-center gap-2.5 flex-shrink-0">
                    <span className={`font-mono text-[10px] ${up ? "text-emerald-400" : "text-red-400"} opacity-70`}>
                      {fmtVal(r.day_change_total, r.country)}
                    </span>
                    <span className={`font-mono font-semibold ${up ? "text-emerald-400" : "text-red-400"}`}>
                      {up ? "+" : ""}{pct.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
